import { create } from "zustand";
import { useEffect } from "react";
import endpoints from "../../utility/endpoints";

let inflight = null;

export const useSasStore = create((set, get) => ({
  sasToken: "",
  sasUrl: "",
  loading: false,
  attempted: false,
  error: null,

  // Fetch Read-Only SAS Token
  fetchReadToken: async () => {
    const { sasToken } = get();
    if (sasToken) return sasToken;
    if (inflight) return inflight; // dedupe concurrent calls

    set({ loading: true, error: null });

    inflight = (async () => {
      try {
              const res = await fetch(`${endpoints.baseUrl}/sas/read`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        if (!data.sasToken) throw new Error("Response missing sasToken");

        set({
          sasToken: data.sasToken,
          sasUrl: data.sasUrl,
          loading: false,
          attempted: true,
        });
        return data.sasToken;
      } catch (err) {
        console.error("Failed to load read SAS token:", err);
        set({ error: err, loading: false, attempted: true });
      } finally {
        inflight = null;
      }
    })();

    return inflight;
  },

  // Builds a blob URL with the current read SAS token
  getBlobUrl: (blobPath) => {
    if (!blobPath) return "";

    const { sasToken, attempted } = get();

    // Trigger token fetch in background if missing (only once, never during render)
    if (!sasToken && !attempted && !inflight && typeof window !== "undefined") {
      queueMicrotask(() => get().fetchReadToken());
    }

    // Full URL: strip any old query string and append current read SAS token
    if (blobPath.startsWith("http://") || blobPath.startsWith("https://")) {
      const baseUrl = blobPath.split("?")[0];
      return sasToken ? `${baseUrl}?${sasToken}` : baseUrl;
    }

    const accountName =
      process.env.NEXT_PUBLIC_ACCOUNT_NAME || "bnlwestgunileveraf01092";
    const containerName = "botstorevideo";

    const cleanPath = blobPath.startsWith("/") ? blobPath.slice(1) : blobPath;
    const encodedPath = cleanPath
      .split("/")
      .map((part) => encodeURIComponent(part))
      .join("/");
    const baseUrl = `https://${accountName}.blob.core.windows.net/${containerName}/${encodedPath}`;
    return sasToken ? `${baseUrl}?${sasToken}` : baseUrl;
  },

  // Request a short-lived full-access/admin token when needed
  fetchAdminToken: async (minutes = 15) => {
    try {
            const res = await fetch(`${endpoints.baseUrl}/sas/admin?minutes=${minutes}`);

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to fetch admin token");
      return data;
    } catch (err) {
      console.error("Error fetching short-lived admin SAS token:", err);
      throw err;
    }
  },
}));

export const useSas = () => {
  const sasToken = useSasStore((s) => s.sasToken);
  const sasUrl = useSasStore((s) => s.sasUrl);
  const loading = useSasStore((s) => s.loading);
  const error = useSasStore((s) => s.error);
  const attempted = useSasStore((s) => s.attempted);
  const fetchReadToken = useSasStore((s) => s.fetchReadToken);
  const getBlobUrl = useSasStore((s) => s.getBlobUrl);
  const fetchAdminToken = useSasStore((s) => s.fetchAdminToken);

  // Fetch once if missing when the hook is used
  useEffect(() => {
    if (!sasToken && !loading && !attempted) {
      fetchReadToken();
    }
  }, [sasToken, loading, attempted, fetchReadToken]);

  return {
    sasToken,
    sasUrl,
    loading,
    error,
    getBlobUrl,
    fetchAdminToken,
    fetchReadToken,
  };
};
